import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MobileShell from "@/components/MobileShell";
import AdminDisplayPreference, { adminShellClassName, useAdminDisplayPreference } from "@/components/AdminDisplayPreference";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Trash2 } from "lucide-react";
import AdminHomeButton from "@/components/AdminHomeButton";

const CATEGORY_LABELS = {
  bug: "Bug",
  confusing: "Confusing",
  suggestion: "Suggestion",
  other: "Other",
};

export default function AdminFeedback() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayPreference, setDisplayPreference] = useAdminDisplayPreference();

  const loadFeedback = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/feedback");
      setItems(data);
    } catch (err) {
      toast.error("Unable to load feedback.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, []);

  const sortedItems = useMemo(
    () => [...items].sort((a, b) => String(b.created_at || "").localeCompare(String(a.created_at || ""))),
    [items]
  );

  const markReviewed = async (feedbackId) => {
    try {
      const { data } = await api.put(`/admin/feedback/${feedbackId}/reviewed`);
      setItems((current) => current.map((item) => (item.id === feedbackId ? data : item)));
      toast.success("Feedback marked reviewed");
    } catch (err) {
      toast.error("Unable to update feedback.");
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm("Delete this feedback item?")) return;
    try {
      await api.delete(`/admin/feedback/${feedbackId}`);
      setItems((current) => current.filter((item) => item.id !== feedbackId));
      toast.success("Feedback deleted");
    } catch (err) {
      toast.error("Unable to delete feedback.");
    }
  };

  return (
    <MobileShell contentClassName={adminShellClassName(displayPreference)}>
      <header className="px-6 pt-8 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <button onClick={() => navigate("/admin")} className="text-slate-500 mb-3" data-testid="admin-feedback-back-btn">
              <ArrowLeft size={20} />
            </button>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-600">Clinic admin</p>
            <h1 className="text-2xl font-bold font-display text-slate-900 mt-1">Beta feedback</h1>
            <p className="text-sm text-slate-500 mt-1">{items.length} items submitted by beta testers</p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <AdminHomeButton />
            <AdminDisplayPreference value={displayPreference} onChange={setDisplayPreference} />
            <Button size="sm" variant="outline" onClick={loadFeedback}>Refresh</Button>
          </div>
        </div>
      </header>

      <section className="px-6 pb-8">
        {loading ? (
          <div className="rounded-2xl border border-slate-100 bg-white p-4 text-sm text-slate-500">Loading feedback...</div>
        ) : sortedItems.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">No beta feedback yet.</div>
        ) : (
          <div className="space-y-3" data-testid="admin-feedback-list">
            {sortedItems.map((item) => (
              <article key={item.id} className="rounded-2xl border border-slate-100 bg-white p-4">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-slate-900">{item.patient_name || "Unknown patient"}</h2>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {CATEGORY_LABELS[item.category] || item.category || "Other"}
                      </span>
                      <StatusPill status={item.status} />
                    </div>
                    <div className="mt-1 text-xs text-slate-500">
                      {item.patient_email || "No email"}{item.created_at ? ` - ${formatDateTime(item.created_at)}` : ""}
                    </div>
                    {item.source_page ? <div className="mt-1 text-xs text-slate-500">Source: {item.source_page}</div> : null}
                    <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-800">{item.message}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {item.status !== "reviewed" ? (
                      <Button size="sm" variant="outline" onClick={() => markReviewed(item.id)}>
                        <CheckCircle2 size={16} /> Mark reviewed
                      </Button>
                    ) : null}
                    <Button size="sm" variant="destructive" onClick={() => deleteFeedback(item.id)}>
                      <Trash2 size={16} /> Delete
                    </Button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </MobileShell>
  );
}

function StatusPill({ status }) {
  const reviewed = status === "reviewed";
  return (
    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${reviewed ? "bg-teal-50 text-teal-700" : "bg-amber-50 text-amber-700"}`}>
      {reviewed ? "Reviewed" : "New"}
    </span>
  );
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value).slice(0, 16);
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
